import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { catalogueReports } from "../api";
import { userCataloguesService } from "../api/userCatalogues.service";
import "../assets/styles/pages/catalogues.scss";
import SelectionTable from "../components/Catalogues/SelectionTable";
import MainStructure from "../components/containers/MainStructure";
import CatalogueCard from "../components/elements/CatalogueCard";
import SearchInput from "../components/elements/SearchInput";

export function mergeCatalogueCollections(available = [], purchased = []) {
	const purchasesByCatalogue = new Map();
	purchased.forEach((purchase, purchaseIndex) => {
		const catalogue = purchase?.catalogue;
		if (catalogue?.id == null) return;
		const existing = purchasesByCatalogue.get(catalogue.id) || {
			purchases: [],
			purchaseIndex,
		};
		existing.purchases.push(purchase);
		purchasesByCatalogue.set(catalogue.id, existing);
	});

	const merged = available.map((catalogue) => {
		const purchaseGroup = purchasesByCatalogue.get(catalogue.id);
		return {
			catalogue,
			purchase: purchaseGroup?.purchases[0] || null,
			purchases: purchaseGroup?.purchases || [],
			purchaseIndex: purchaseGroup?.purchaseIndex ?? Number.MAX_SAFE_INTEGER,
		};
	});
	const availableIds = new Set(available.map((catalogue) => catalogue.id));

	purchased.forEach((purchase, purchaseIndex) => {
		if (purchase?.catalogue && !availableIds.has(purchase.catalogue.id)) {
			const purchaseGroup = purchasesByCatalogue.get(purchase.catalogue.id);
			if (purchaseGroup?.purchaseIndex === purchaseIndex) {
				merged.push({
					catalogue: purchase.catalogue,
					purchase: purchaseGroup.purchases[0],
					purchases: purchaseGroup.purchases,
					purchaseIndex,
				});
			}
		}
	});

	return merged.sort((first, second) => {
		if (Boolean(first.purchase) !== Boolean(second.purchase)) {
			return first.purchase ? 1 : -1;
		}
		if (first.purchase && second.purchase) {
			return first.purchaseIndex - second.purchaseIndex;
		}
		return String(first.catalogue?.name || "").localeCompare(
			String(second.catalogue?.name || "")
		);
	});
}

function Catalogues() {
	const location = useLocation();
	const navigate = useNavigate();
	const [view, setView] = useState("all");
	const [catalogues, setCatalogues] = useState([]);
	const [searchQuery, setSearchQuery] = useState("");
	const [loading, setLoading] = useState(true);
	const [loadError, setLoadError] = useState(false);
	const [refresh, setRefresh] = useState(0);
	const [selectedCatalogue, setSelectedCatalogue] = useState(null);
	const cacheRef = useRef({});
	const buyCatalogueId = new URLSearchParams(location.search).get("buy");

	useEffect(() => {
		if (location.state?.filter !== "myCatalogues") return;
		cacheRef.current = {};
		setView("purchased");
		setSelectedCatalogue(null);
		setRefresh((previous) => previous + 1);
		navigate(location.pathname, { replace: true });
	}, [location.pathname, location.state, navigate]);

	useEffect(() => {
		let cancelled = false;
		const timer = window.setTimeout(async () => {
			const search = searchQuery.trim();
			if (cacheRef.current[search]) {
				setCatalogues(cacheRef.current[search]);
				setLoadError(false);
				setLoading(false);
				return;
			}

			setLoading(true);
			const [availableResult, purchasedResult] = await Promise.allSettled([
				catalogueReports.list(search),
				userCataloguesService.list(search),
			]);
			if (cancelled) return;

			if (
				availableResult.status === "rejected" &&
				purchasedResult.status === "rejected"
			) {
				setLoadError(true);
				setCatalogues([]);
				setLoading(false);
				return;
			}

			const available =
				availableResult.status === "fulfilled"
					? availableResult.value?.results || []
					: [];
			const purchased =
				purchasedResult.status === "fulfilled"
					? purchasedResult.value?.results || []
					: [];
			const merged = mergeCatalogueCollections(available, purchased);
			cacheRef.current[search] = merged;
			setCatalogues(merged);
			setLoadError(
				availableResult.status === "rejected" ||
					purchasedResult.status === "rejected"
			);
			setLoading(false);
		}, 350);

		return () => {
			cancelled = true;
			window.clearTimeout(timer);
		};
	}, [refresh, searchQuery]);

	const purchasedCount = useMemo(
		() => catalogues.filter((item) => item.purchase).length,
		[catalogues]
	);
	const visibleCatalogues = useMemo(
		() =>
			view === "purchased"
				? catalogues.filter((item) => item.purchase)
				: catalogues,
		[catalogues, view]
	);

	useEffect(() => {
		if (!buyCatalogueId || loading) return;
		const catalogue = catalogues.find((item) => String(item.catalogue.id) === buyCatalogueId);
		if (catalogue) setSelectedCatalogue(catalogue);
	}, [buyCatalogueId, catalogues, loading]);

	if (selectedCatalogue) {
		return (
			<SelectionTable
				catalogueId={selectedCatalogue.catalogue.id}
				catalogueName={selectedCatalogue.catalogue.name}
				catalogueDescription={selectedCatalogue.catalogue.description}
				purchaseIds={selectedCatalogue.purchases.map((purchase) => purchase.id)}
				onBack={() => {
					setSelectedCatalogue(null);
					if (buyCatalogueId) navigate("/catalogues", { replace: true });
				}}
			/>
		);
	}

	return (
		<MainStructure>
			<main className="catalogue-library">
				<header className="catalogue-library__hero">
					<div>
						<p className="catalogue-library__eyebrow">Auction analysis</p>
						<h1>Catalogue reports</h1>
						<p>
							Browse auction lots, explore pedigree insights, and compare
                            your purchased horses side by side.
						</p>
					</div>
					<div className="catalogue-library__summary" aria-label="Catalogue summary">
						<span><strong>{catalogues.length}</strong> catalogues</span>
						<span><strong>{purchasedCount}</strong> purchased</span>
					</div>
				</header>

				<section className="catalogue-library__panel" aria-labelledby="catalogue-list-title">
					<div className="catalogue-library__toolbar">
						<div>
							<h2 id="catalogue-list-title">Your catalogue library</h2>
							<p>Purchase status and the next action are shown on every catalogue.</p>
						</div>
						<SearchInput
							value={searchQuery}
							onChange={(event) => setSearchQuery(event.target.value)}
							placeholder="Search catalogues"
							ariaLabel="Search catalogues"
						/>
					</div>

					<div className="catalogue-library__views" role="group" aria-label="Catalogue view">
						<button
							type="button"
							className={view === "all" ? "is-active" : ""}
							onClick={() => setView("all")}
							aria-pressed={view === "all"}
						>
							All catalogues <span>{catalogues.length}</span>
						</button>
						<button
							type="button"
							className={view === "purchased" ? "is-active" : ""}
							onClick={() => setView("purchased")}
							aria-pressed={view === "purchased"}
						>
							My purchases <span>{purchasedCount}</span>
						</button>
					</div>

					{loadError ? (
						<div className="catalogue-library__notice" role="status">
							<i className="fa fa-exclamation-circle" aria-hidden="true" />
							<span>Some catalogue information could not be loaded. Try refreshing the page.</span>
						</div>
					) : null}

					{loading ? (
						<div className="catalogue-library__loading" role="status">
							<span className="catalogue-library__spinner" />
							Loading catalogues…
						</div>
					) : visibleCatalogues.length ? (
						<div className="catalogue-list">
							{visibleCatalogues.map((item) => (
								<CatalogueCard
									key={item.catalogue.id}
									catalogue={item.catalogue}
									purchase={item.purchase}
									purchases={item.purchases}
									onSelect={() => setSelectedCatalogue(item)}
								/>
							))}
						</div>
					) : (
						<div className="catalogue-library__empty">
							<i className="fa fa-book-open" aria-hidden="true" />
							<h3>{view === "purchased" ? "No purchases yet" : "No catalogues found"}</h3>
							<p>
								{searchQuery.trim()
									? `No catalogues match “${searchQuery.trim()}”.`
									: view === "purchased"
										? "Catalogues you purchase will appear here with their analysis status."
										: "There are no available catalogues at the moment."}
							</p>
							{view === "purchased" && !searchQuery.trim() ? (
								<button type="button" onClick={() => setView("all")}>Browse catalogues</button>
							) : null}
						</div>
					)}
				</section>
			</main>
		</MainStructure>
	);
}

export default Catalogues;
